import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { Counter } from "PFApp/components/counter";
import { SearchInput } from "PFApp/components/search_input";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getValueFilters } from "../../../use_filtered_collection/format_filters";
import { useActiveCounter } from "../use_active_counter";
import css from "./engagements_header.module.scss";

type CounterCode = "new" | "sold" | "starting_soon" | "draft" | "complete" | "total";

type Tile = {
  name: string;
  code: CounterCode;
  separator?: boolean;
  filters: {
    fields: Record<string, string[] | null>;
  };
};

type EngagementsHeaderProps = {
  className?: string;
};

const EngagementsHeader = ({ className }: EngagementsHeaderProps): JSX.Element => {
  const {
    store: {
      pages: {
        workflowPage: {
          engagements: {
            data: { meta },
            counters: fetchedCounters,
            onFilterChange,
            onFiltersReset
          }
        }
      }
    }
  } = useContext(AppContext);

  const stringify = useDeterministicStringify();

  const { t } = useTranslation("workflow");
  const valueFilters = getValueFilters(meta.filters);
  const termFilter = meta.filters?.fields?.term;
  const handleTermChange = (term) => onFilterChange(termFilter, term);

  const counters = useMemo(() => {
    const tiles: Tile[] = [
      {
        name: t("parts.counters.totalOpen"),
        code: "new",
        filters: {
          fields: {
            state: ["new"]
          }
        }
      },
      {
        name: t("parts.counters.draft"),
        code: "draft",
        filters: {
          fields: {
            state: ["draft"]
          }
        }
      },
      {
        name: t("parts.counters.closed"),
        code: "complete",
        filters: {
          fields: {
            state: ["complete"]
          }
        }
      }
    ];
    return tiles;
  }, [t]);

  const activeCounter = useActiveCounter(counters, meta);

  return (
    <div className={classNames(css.container, className)}>
      <div className={css.scrollWrapper}>
        <SearchInput
          term={termFilter?.value || ""}
          onTermChange={handleTermChange}
          variant="gray"
          qaId="WorkflowPageEngagementSearch"
        />
        <div className={css.counters}>
          {counters.map(({ name, code, separator, filters }) => (
            <React.Fragment key={`${code}_${stringify(valueFilters.fields)}`}>
              <Counter
                title={name}
                code={code}
                selected={activeCounter === code}
                counter={fetchedCounters[code]}
                onClick={() => {
                  onFiltersReset({
                    ...valueFilters,
                    fields: {
                      ...valueFilters.fields,
                      ...filters.fields
                    }
                  });
                }}
                qaIdPrefix="WorkflowPageEngagementCounter"
              />
              {separator && <div className={css.separator} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

EngagementsHeader.propTypes = {
  className: PropTypes.string
};

export default EngagementsHeader;
